'use client';
import { Banner } from '@airbus/components-react';
import Spinners from '../../shared/components/Spinner';
import GridTable from '../../shared/components/grid/GridTable';
import Layout from '../../shared/components/layout';
import useFetchScreenDetails from '../../shared/utils/useFetchScreenDetails';
import { useCallback, useEffect, useState } from 'react';
import GetAllColumnContextProvider, {
  useColumnContextAPI,
} from '../../shared/context/GetAllColumnContext';
import ScreenTableColumnConfig from '../../features/table-column-configuration/components/ScreenTableColumnConfig';
import ComboBoxForScreens from '../../features/table-column-configuration/components/ComboBoxForScreens';
import { CommitHandler } from '../../features/table-column-configuration/CommitHandler';
import { formOrderValidator } from '../../features/table-column-configuration/formOrderValidator';
import {
  customSorting,
  defaultValueColumnDef,
} from '../../features/table-column-configuration/TableColumnConfigHelper';
import { useConfigContext } from '../../shared/context/ConfigContext';
import { useLanguageContextAPI } from '../../shared/context/LanguageContext';
import { getAllTableColumnConfigQuery } from '../../features/table-column-configuration/services/getAllTableColumnConfigQuery';

const ScreenTableConfigurationPage = () => {
  const { loading, error, screenTables } = useFetchScreenDetails('grid_attribute_config');
  return (
    <>
      {loading ? (
        <Spinners />
      ) : (
        <Layout>
          {error && <Banner variant='warning'>Data is not available</Banner>}
          {!screenTables
            ? null
            : screenTables.map((item) => (
                <GetAllColumnContextProvider key={item.id} isDirectConfigScreen={true}>
                  <ScreensAccess tableRes={item} />
                </GetAllColumnContextProvider>
              ))}
        </Layout>
      )}
    </>
  );
};

export default ScreenTableConfigurationPage;

const ScreensAccess = ({ tableRes }) => {
  const { configSchema } = useConfigContext();
  const [customColumns, setCustomColumns] = useState([]);
  const [customRows, setCustomRows] = useState([]);
  const [tableId, setTableId] = useState(null);
  const { language } = useLanguageContextAPI();
  const {
    updateCustomConfig,
    allColumns,
    allDropdowns,
    accessDetails: access,
    reRender,
  } = useColumnContextAPI();
  const fetchRowData = useCallback(async () => {
    if (tableId) {
      const rowData = await getRowsData({ setCustomRows, configSchema, tableRes, tableId });
      setCustomRows(rowData);
    }
  }, [tableId]);
  useEffect(() => {
    if (access && allColumns?.length && allDropdowns) {
      const gridColumns = getCustomConfigs({
        allColumns,
        language,
        allDropdowns,
        access,
        setCustomRows,
      });
      setCustomColumns(gridColumns);
    }
  }, [allColumns, access, allDropdowns]);
  useEffect(() => {
    fetchRowData();
  }, [fetchRowData, reRender]);
  useEffect(() => {
    if (allDropdowns) {
      updateCustomConfig({
        AdditionalHeaderForScreens: [
          <ComboBoxForScreens
            setTableId={setTableId}
            screenGridId={tableRes.id}
            key='screenCombo'
          />,
        ],
        CommitHandler,
        SetIsCellEditable,
        uniqueValidator: formOrderValidator,
        ScreenSlotProps: {
          customSorting,
          setTableId,
          customColumns,
        },
      });
    }
  }, [allDropdowns]);

  useEffect(() => {
    if (customColumns && customRows) {
      updateCustomConfig({
        ScreenSlotProps: {
          customColumns,
          customRows,
          setCustomRows,
        },
      });
    }
  }, [customColumns, customRows]);
  return <GridTable tableRes={tableRes} />;
};
export function SetIsCellEditable(props) {
  const { field, row, access } = props;
  let isEditable = true;
  if (
    (field === 'attr_group_id' && row.attr_group_id == null) ||
    (field === 'form_order_no' && row.form_order_no == null)
  ) {
    isEditable = false;
    return isEditable;
  }
  Object.assign(access, {
    default_value: { accessLevel: 'U', featureName: 'default_value', screenGridId: 16 },
  });
  const rolePermission = access?.[field]?.accessLevel === 'U';
  return row.status !== 'D' && access?.edit_row && isEditable && rolePermission;
}

const getRowsData = async (props) => {
  const { tableRes, configSchema, tableId } = props;
  const res = await getAllTableColumnConfigQuery({
    screenGridId: tableRes.id,
    configSchema,
    tableId,
  });
  if (!res?.[`${configSchema}_v_screen_grid_attribute_config`]) {
    return [];
  }
  const allRows = res[`${configSchema}_v_screen_grid_attribute_config`];
  const arrData = getTableRowData({ allRows, tableRes });
  return arrData;
};

const getCustomConfigs = (props) => {
  const { allColumns, language, access, allDropdowns, setCustomRows } = props;
  const removeColumns = [
    'created_by',
    'updated_by',
    'insert_date',
    'update_date',
    'screen_id',
    'screen_grid_id',
    'attribute_config_id',
  ];
  const filteredColumn = allColumns.filter(
    (item) => !removeColumns.includes(item.attrInternalName),
  );
  filteredColumn.splice(5, 0, defaultValueColumnDef);
  const { gridColumns } = ScreenTableColumnConfig({
    allColumns: [...filteredColumn],
    access: {
      ...access,
      default_value: { accessLevel: 'U', featureName: 'default_value', screenGridId: 16 },
    },
    language,
    allDropdowns,
    setCustomRows,
  });
  return gridColumns;
};

const getTableRowData = (props) => {
  const { allRows, tableRes } = props;
  const dArray = allRows?.map((result, index) => {
    result.default_value = result.attr_duplicate_config;
    if (result.attr_duplicate_config !== 'copy-existing') {
      result.attr_duplicate_config = '';
    }

    if (result.attr_duplicate_config === 'copy-existing') {
      result.default_value = '';
    }
    const data = { status: '', backup: { ...result }, ...result };
    if (tableRes.mbdDataGrid.dataGridUniqueIdentifier) {
      data.id = result[tableRes.mbdDataGrid.dataGridUniqueIdentifier];
    } else {
      data.id = index + 1;
    }
    return data;
  });
  return dArray;
};
